// src/components/Editor.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ANSWER_TEMPLATE,
  normalizeAnswerTemplate,
} from "../constants/answerTemplates";
import { getAnswerLayout } from "../utils/answerLayout";

const Editor = React.forwardRef(
  (
    {
      specialChar,
      scale,
      isPreviewMode,
      isTransitioning,
      editorRef,

      // 기존 plain text content
      fetchedContent,

      // 앞으로 추가될 필드들
      // content_html
      fetchedContentHtml,

      // content_text
      fetchedContentText,

      templateType,
    },
    ref,
  ) => {
    const normalizedTemplateType = normalizeAnswerTemplate(templateType);
    const isMinistryTemplate =
      normalizedTemplateType === ANSWER_TEMPLATE.MINISTRY_OF_JUSTICE;
    const layout = getAnswerLayout(normalizedTemplateType);
    const { lineHeight, minimumPageCount, pageGroupSize } = layout;

    // 법무부 양식은 처음 2쪽을 표시한 뒤 필요한 만큼 한 쪽씩 계속 늘어난다.
    // 법전협 양식도 기존 4쪽 단위는 유지하되 최대 쪽수 제한은 두지 않는다.
    const [lineNumbers, setLineNumbers] = useState([]);
    const [pageCount, setPageCount] = useState(minimumPageCount);
    const loadedContentKeyRef = useRef(null);

    useEffect(() => {
      setPageCount(minimumPageCount);
    }, [minimumPageCount]);

    /**
     * 기존 plain text 답안을 HTML로 변환할 때,
     * <, >, &, " 같은 문자가 HTML로 해석되지 않도록 escape 처리
     */
    const escapeHtml = useCallback((text) => {
      const div = document.createElement("div");
      div.innerText = text;
      return div.innerHTML;
    }, []);

    /**
     * 기존 content/plain text를 contentEditable에서 사용할 수 있는 HTML로 변환
     *
     * 예:
     * "제1문\n원고는 피고에게..."
     *
     * ->
     *
     * <div>제1문</div><div>원고는 피고에게...</div>
     */
    const convertTextToHtml = useCallback(
      (text) => {
        if (!text) {
          return "<div><br></div>";
        }

        return text
          .split("\n")
          .map((line) => {
            const escapedLine = escapeHtml(line);
            return `<div>${escapedLine || "<br>"}</div>`;
          })
          .join("");
      },
      [escapeHtml],
    );

    const getPageMaxLines = useCallback(
      (page) => (page === 1 ? 30 : 34),
      [],
    );

    const getHeightByPageCount = useCallback(
      (targetPageCount) => {
        let height = 0;

        for (let page = 1; page <= targetPageCount; page++) {
          height += getPageMaxLines(page) * lineHeight;
        }

        return height;
      },
      [getPageMaxLines, lineHeight],
    );

    const getTotalLinesByPageCount = useCallback(
      (targetPageCount) => {
        let total = 0;

        for (let page = 1; page <= targetPageCount; page++) {
          total += getPageMaxLines(page);
        }

        return total;
      },
      [getPageMaxLines],
    );

    const getPageCountByLines = useCallback(
      (totalLines) => {
        let page = 1;
        let remainingLines = totalLines;

        while (remainingLines > getPageMaxLines(page)) {
          remainingLines -= getPageMaxLines(page);
          page++;
        }

        return page;
      },
      [getPageMaxLines],
    );

    const getExpandedPageCount = useCallback(
      (actualPageCount) =>
        Math.max(
          minimumPageCount,
          Math.ceil(actualPageCount / pageGroupSize) * pageGroupSize,
        ),
      [minimumPageCount, pageGroupSize],
    );

    const getBjhStyle = useCallback(
      (targetPageCount) => {
        let colorStops = "";
        let currentHeight = 0;

        for (let page = 1; page <= targetPageCount; page++) {
          const pageHeight = getPageMaxLines(page) * lineHeight;
          const nextHeight = currentHeight + pageHeight;

          if (isMinistryTemplate) {
            const pageColor =
              page % 2 === 1
                ? "rgba(247, 247, 247, 0.8)"
                : "rgba(227, 235, 241, 0.8)";
            colorStops += `${pageColor} ${currentHeight}px, ${pageColor} ${nextHeight}px`;
          } else {
            const color =
              page % 2 === 1
                ? "rgba(255, 255, 255, 1)"
                : "rgba(243, 247, 255, 1)";
            colorStops += `${color} ${currentHeight}px, ${color} ${nextHeight}px`;
          }

          if (page !== targetPageCount) {
            colorStops += ", ";
          }

          currentHeight = nextHeight;
        }

        if (isMinistryTemplate) {
          return `linear-gradient(to bottom, ${colorStops}), repeating-linear-gradient(
            transparent,
            transparent 23px,
            rgb(25, 22, 43) 23px,
            rgb(25, 22, 43) 24px
          )`;
        }

        return `repeating-linear-gradient(
          transparent,
          transparent 39px,
          rgb(238, 238, 238) 39px,
          rgb(238, 238, 238) 40px
        ), linear-gradient(${colorStops})`;
      },
      [getPageMaxLines, isMinistryTemplate, lineHeight],
    );

    const calculateVisibleLines = useCallback(() => {
      if (!editorRef.current) return;

      // minHeight는 빈 답안에도 현재 양식의 전체 쪽 높이를 부여한다. 양식을
      // 전환할 때 그 값을 실제 작성량으로 오인하지 않도록 잠시 제외해 잰다.
      const renderedMinHeight = editorRef.current.style.minHeight;
      editorRef.current.style.minHeight = "0px";
      const editorHeight = Math.max(editorRef.current.scrollHeight, lineHeight);
      editorRef.current.style.minHeight = renderedMinHeight;
      const totalLines = Math.max(Math.floor(editorHeight / lineHeight), 1);

      const actualPageCount = getPageCountByLines(totalLines);
      const nextPageCount = getExpandedPageCount(actualPageCount);

      const visibleTotalLines = getTotalLinesByPageCount(nextPageCount);

      const newLineNumbers = [];

      let currentPage = 1;
      let currentPageLine = 0;
      let currentPageMaxLines = getPageMaxLines(currentPage);

      for (let i = 0; i < visibleTotalLines; i++) {
        if (currentPageLine >= currentPageMaxLines) {
          currentPage++;
          currentPageLine = 0;
          currentPageMaxLines = getPageMaxLines(currentPage);
        }

        currentPageLine++;

        newLineNumbers.push({
          page: currentPage,
          line: currentPageLine,
          absoluteLine: i + 1,
        });
      }

      setPageCount(nextPageCount);
      setLineNumbers(newLineNumbers);
    }, [
      editorRef,
      getPageCountByLines,
      getExpandedPageCount,
      getTotalLinesByPageCount,
      getPageMaxLines,
      lineHeight,
    ]);

    useEffect(() => {
      if (specialChar && editorRef.current) {
        const selection = window.getSelection();

        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);

          const textNode = document.createTextNode(specialChar);
          range.insertNode(textNode);

          range.setStartAfter(textNode);
          range.setEndAfter(textNode);
          selection.removeAllRanges();
          selection.addRange(range);

          requestAnimationFrame(() => {
            calculateVisibleLines();
          });
        }
      }
    }, [specialChar, editorRef, calculateVisibleLines]);

    useEffect(() => {
      calculateVisibleLines();
    }, [calculateVisibleLines]);

    /**
     * 답안 불러오기
     *
     * 우선순위:
     * 1. fetchedContentHtml 있으면 그대로 HTML 로드
     * 2. fetchedContentText 있으면 plain text -> HTML 변환
     * 3. 기존 fetchedContent 있으면 plain text -> HTML 변환
     * 4. 아무것도 없으면 빈 답안
     */
    useEffect(() => {
      if (!editorRef.current) return;

      const contentKey = JSON.stringify([
        fetchedContentHtml || "",
        fetchedContentText || "",
        fetchedContent || "",
      ]);

      // 양식만 전환될 때 사용자가 작성 중인 DOM을 빈 fetchedContent로 덮지 않는다.
      if (loadedContentKeyRef.current === contentKey) {
        requestAnimationFrame(() => {
          calculateVisibleLines();
        });
        return;
      }

      const hasHtml = fetchedContentHtml && fetchedContentHtml.trim() !== "";

      const htmlToLoad = hasHtml
        ? fetchedContentHtml
        : convertTextToHtml(fetchedContentText || fetchedContent || "");

      editorRef.current.innerHTML = htmlToLoad;
      loadedContentKeyRef.current = contentKey;

      requestAnimationFrame(() => {
        calculateVisibleLines();
      });
    }, [
      fetchedContentHtml,
      fetchedContentText,
      fetchedContent,
      editorRef,
      convertTextToHtml,
      calculateVisibleLines,
    ]);

    const editorMinHeight = getHeightByPageCount(pageCount);
    // 법무부 실제 답안 화면의 원본 CSS 폭(784px)을 따른다.
    // 로컬 웹폰트와 자간을 함께 고정해 OS와 무관하게 한글 49자까지 들어간다.
    const editorWidth = layout.width;
    const editorPadding = layout.padding;
    const topSpacerHeight = layout.topSpacerHeight;
    const headerHeight = isMinistryTemplate ? 0 : 80;
    const lineNumberWidth = layout.gutterWidth;
    const shouldShowLineNumber = (line) =>
      isMinistryTemplate
        ? line.line === 1 || line.absoluteLine % 5 === 0
        : line.line % 2 === 1;

    return (
      <div>
        <div
          style={{
            display: "flex",
            borderRadius: "15px",
            transform: `scale(${scale})`,
            transformOrigin: "center 0",
            transition: "transform 0.2s ease",
          }}
        >
          <div style={{ width: `${lineNumberWidth}px`, flexShrink: 0 }}>
            <div style={{ height: `${topSpacerHeight}px` }} />

            <div style={{ height: `${headerHeight}px`, width: "12px" }} />

            {lineNumbers.map((line, index) => (
              <p
                key={index}
                style={{
                  color: shouldShowLineNumber(line) ? "#5f6b7a" : "transparent",
                  lineHeight: `${lineHeight}px`,
                  fontSize: isMinistryTemplate ? "10px" : "11px",
                  fontWeight:
                    isMinistryTemplate && shouldShowLineNumber(line)
                      ? 700
                      : 400,
                  margin: 0,
                  textAlign: isMinistryTemplate ? "right" : "left",
                  paddingRight: isMinistryTemplate ? "10px" : 0,
                  whiteSpace: "nowrap",
                }}
              >
                {isMinistryTemplate && line.line === 1
                  ? `(제1문-${line.page}쪽) ${line.absoluteLine}`
                  : isMinistryTemplate
                    ? `${line.absoluteLine}`
                  : `${line.line} ${line.line === 1 ? `(${line.page}쪽)` : ""}`}
              </p>
            ))}
          </div>

          <div
            ref={ref}
            className={`answer-print-root answer-template-${normalizedTemplateType}`}
          >
            <div style={{ height: `${topSpacerHeight}px` }} />
            {!isMinistryTemplate && (
              <>
                <div
                  style={{
                    height: "40px",
                    backgroundColor: "#003664",
                    borderRadius: "10px 10px 0px 0px",
                    textAlign: "center",
                  }}
                >
                  <span
                    style={{
                      color: "white",
                      fontWeight: "700",
                      fontSize: "24px",
                      lineHeight: "40px",
                      display: "inline-block",
                    }}
                  >
                    문형
                  </span>
                </div>
                <div
                  style={{
                    height: "40px",
                    backgroundColor: "#E3EFFF",
                    textAlign: "center",
                  }}
                >
                  <span
                    style={{
                      color: "#003664",
                      fontWeight: "700",
                      fontSize: "24px",
                      lineHeight: "40px",
                      display: "inline-block",
                    }}
                  >
                    사례형
                  </span>
                </div>
              </>
            )}
            <div
              className="page-wrapper"
                style={{
                  boxSizing: "border-box",
                  width: `${editorWidth}px`,
                padding: `0 ${editorPadding}px`,
                minHeight: `${editorMinHeight}px`,
                margin: "0 auto",
                fontFamily: '"Noto Sans KR", "Malgun Gothic", sans-serif',
                border: "none",
                backgroundImage: getBjhStyle(pageCount),
                backgroundRepeat: "no-repeat",
                boxShadow: "none",
              }}
            >
              <div
                contentEditable={!isPreviewMode}
                suppressContentEditableWarning={true}
                spellCheck="false"
                ref={editorRef}
                onInput={calculateVisibleLines}
                className="page editor"
                style={{
                  ...layout.textStyle,
                  minHeight: `${editorMinHeight}px`,
                  outline: "none",
                  transition: "opacity 0.3s ease, transform 0.2s ease",
                  opacity: isTransitioning ? 0 : 1,
                  fontSmoothing: "antialiased",
                }}
              />
            </div>
          </div>
        </div>

        <div style={{ height: "100px" }} />

        <style>
          {`
    @media print {
      html,
      body {
        margin: 0 !important;
        padding: 0 !important;
        background: white !important;
      }

      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }

      @page {
        size: A4;
        margin: 8.78mm;
      }

      .answer-print-root {
        width: 974px !important;
        margin: 0 auto !important;
        padding: 0 !important;
        transform: none !important;
        transform-origin: top center !important;
        background: white !important;
      }

      .answer-print-root .page-wrapper {
        width: 938px !important;
        padding: 0 18px !important;
        margin: 0 auto !important;
        background-repeat: no-repeat !important;
        background-size: 100% auto !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }

      .answer-print-root .page.editor {
        font-size: 20px !important;
        line-height: 40px !important;
        white-space: pre-wrap !important;
        outline: none !important;
        opacity: 1 !important;
        transform: none !important;
        overflow: visible !important;
      }

      .answer-template-ministry_of_justice {
        width: 784px !important;
      }

      .answer-template-ministry_of_justice .page-wrapper {
        width: 784px !important;
        padding: 0 !important;
      }

      .answer-template-ministry_of_justice .page.editor {
        font-family: "IBT Noto Sans KR", "Malgun Gothic", sans-serif !important;
        font-size: 16px !important;
        line-height: 24px !important;
        letter-spacing: 1.25px !important;
        word-break: break-all !important;
        line-break: anywhere !important;
        overflow-wrap: break-word !important;
        white-space: normal !important;
      }

      .answer-print-root [contenteditable="true"] {
        caret-color: transparent !important;
      }

      .answer-print-root div,
      .answer-print-root span,
      .answer-print-root p {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
    }
  `}
        </style>
      </div>
    );
  },
);

export default Editor;
